// Here For design tokens.
//
// Framework-agnostic on purpose: plain TypeScript values plus a CSS custom-property
// emitter (see css.ts). Nothing here imports React, Convex, or the product-language
// package — a token is a value, not a component and not a string of copy.
//
// The palette starts from the plan's provisional tokens (section 21.3), which state
// outright that "exact values require final contrast validation in implementation".
// That validation is contrast.test.ts, and where a provisional value failed AA the
// adjustment is recorded in the comment beside it.

/** Raw palette. Prefer the semantic tokens below; these exist so semantics can be re-pointed. */
export const palette = {
  canvas: "#F6F7F4", // warm neutral, plan 21.3
  surface: "#FFFFFF",
  surfaceSubtle: "#EEF2EF",

  ink: "#17211D",
  inkMuted: "#5B6862",

  border: "#D7DED9",
  // 21.3's `border` is a decorative hairline (1.5:1) and cannot carry a control
  // outline: SC 1.4.11 requires 3:1 for UI components. This value is the darkened
  // companion, chosen by solving the contrast equation against `canvas` (the harder
  // of the two page surfaces), not by eye. Measures 4.43:1 on canvas, 4.76:1 on
  // surface — comfortably clear of the 3:1 floor.
  borderStrong: "#68766F",

  accent: "#176B5B", // deep, calm teal
  accentHover: "#125649",
  accentSurface: "#E6F0ED", // added: tinted background for accent chips

  success: "#267247",
  successSurface: "#E4F0E8",

  warning: "#9A5A00",
  warningSurface: "#FBF0DF",

  danger: "#B42318",
  dangerSurface: "#FCEBE9",

  focus: "#1D4ED8",
} as const;

/** Semantic roles. Components reference these, never the palette directly. */
export const color = {
  canvas: palette.canvas,
  surface: palette.surface,
  surfaceSubtle: palette.surfaceSubtle,

  textPrimary: palette.ink,
  /**
   * Supporting text only. The plan (21.3) is explicit that muted text is never used
   * for essential information at failing contrast — this value clears AA for normal
   * text on every surface token, so "muted" here means de-emphasised, not unreadable.
   */
  textMuted: palette.inkMuted,
  textOnAccent: palette.surface,

  border: palette.border,
  borderStrong: palette.borderStrong,

  accent: palette.accent,
  accentHover: palette.accentHover,

  /** Destructive actions and errors ONLY (plan 21.3). Never a need state — see stateColor. */
  danger: palette.danger,

  /** Focus indication, deliberately a different hue from selection (plan 24.6). */
  focus: palette.focus,
} as const;

/**
 * The five user-visible need states, plus the "Past" display treatment.
 *
 * Keys match `USER_VISIBLE_STATE_KEYS` in @herefor/product-language exactly, but are
 * declared independently: product-language owns the words, design-system owns the
 * pixels, and neither imports the other. `stateTokenKeysMatchProductLanguage` in the
 * test suite pins the two lists together so they cannot drift apart silently.
 *
 * These colours ARE product semantics (bead hf-e2-design-shell-ej8.1) and must never
 * be improvised per screen.
 *
 * Colour is never the only signal: plan 24.6 requires status to carry text, icon or
 * shape as well. These tokens colour a chip that always has a label.
 */
export const stateColor = {
  /**
   * Needs someone — the resting state of an unmet need. Calm and neutral-serious.
   * Deliberately NOT red or amber: this is the normal condition of a new need, not a
   * failure, and the product's tone is non-coercive. Colouring every open need as an
   * alarm is exactly the guilt mechanic the plan forbids.
   */
  needsSomeone: { fg: "#3D4A44", bg: palette.surfaceSubtle, border: palette.borderStrong },

  /** Covered — everything required has been agreed. Success green, per 21.3. */
  covered: { fg: palette.success, bg: palette.successSurface, border: "#478060" },

  /**
   * Please recheck — attention needed, without alarm. Warning amber, NOT danger red:
   * the bead is explicit that this "must read as attention-needed without alarm-red
   * panic", and 21.3 reserves red for destructive/error states. Nobody has done
   * anything wrong when a need changes materially; they are being asked, not scolded.
   */
  pleaseRecheck: { fg: palette.warning, bg: palette.warningSurface, border: "#8F6B20" },

  /** Done — finished and in the past. Quiet, positive, de-emphasised. */
  done: { fg: "#4A5854", bg: palette.surfaceSubtle, border: palette.borderStrong },

  /**
   * Cancelled — not deleted (invariant 6: cancellation preserves history). Muted and
   * plainly inert, so it reads as "this is no longer happening" rather than "error".
   */
  cancelled: { fg: "#5B6862", bg: "#EDEDEB", border: "#717874" },
} as const;

/** "Past" is a display treatment applied over a state, not a sixth state. */
export const pastDisplay = {
  fg: palette.inkMuted,
  bg: palette.canvas,
  border: "#6E7C76",
} as const;

export type StateTokenKey = keyof typeof stateColor;
export type StateColorToken = (typeof stateColor)[StateTokenKey];

/**
 * Type scale, in rem. Base is 1rem so the user's browser setting is respected —
 * never px, which would defeat zoom for the low-vision readers this product expects.
 */
export const fontSize = {
  xs: "0.8125rem",
  sm: "0.9375rem",
  base: "1rem",
  lg: "1.125rem",
  xl: "1.375rem",
  "2xl": "1.75rem",
  "3xl": "2.25rem",
} as const;

export const fontWeight = { regular: 400, medium: 500, semibold: 600 } as const;

export const lineHeight = { tight: 1.25, normal: 1.5, relaxed: 1.65 } as const;

/** 4px-based spacing scale, expressed in rem so it scales with text. */
export const space = {
  0: "0",
  1: "0.25rem",
  2: "0.5rem",
  3: "0.75rem",
  4: "1rem",
  5: "1.5rem",
  6: "2rem",
  7: "3rem",
  8: "4rem",
} as const;

/**
 * Minimum interactive target size.
 *
 * WCAG 2.2 SC 2.5.8 sets an AA floor of 24x24 CSS px. We use 44 because the plan is
 * explicit that "WCAG 2.2's minimum target-size requirement is a floor, not the design
 * aspiration" (section 24.2) — the expected readers include older adults and people
 * with motor impairments or tremor, often on a phone, sometimes in a hospital corridor.
 */
export const targetSize = { minimum: "44px", comfortable: "48px" } as const;

/**
 * Layout measures. Small on purpose — these exist so component CSS never carries a
 * bare length, not to become a layout framework.
 */
export const layout = {
  /** Dialog max width: about 60 characters, which is a comfortable reading measure. */
  dialogMaxWidth: "32rem",
  /** Calendar lane label column. rem, so it grows with the user's text size. */
  laneLabelWidth: "6rem",
} as const;

export const radius = { sm: "0.25rem", md: "0.5rem", lg: "0.75rem", pill: "999px" } as const;

/** Restrained on purpose — the plan forbids ornamental depth (21.2). */
export const elevation = {
  none: "none",
  raised: "0 1px 2px rgba(23, 33, 29, 0.08)",
  overlay: "0 8px 24px rgba(23, 33, 29, 0.16)",
} as const;

/**
 * Motion. Every duration has a reduced-motion counterpart of `0ms`: the contract is
 * that consumers gate on `prefers-reduced-motion`, and css.ts emits that media query
 * so a component author cannot forget.
 */
export const motion = {
  durationFast: "120ms",
  durationBase: "200ms",
  durationSlow: "320ms",
  easingStandard: "cubic-bezier(0.2, 0, 0, 1)",
  reducedDuration: "0ms",
} as const;

/**
 * Focus ring. Distinct in hue from selection (plan 24.6), and thick enough to be seen
 * at low vision. The offset keeps it legible against a same-coloured control.
 */
export const focusRing = {
  width: "3px",
  offset: "2px",
  color: color.focus,

  /**
   * The ring is a PAIR of colours, not one.
   *
   * A single blue ring is invisible on the accent button: #1D4ED8 on #176B5B is
   * 1.05:1 — the contrast test caught this, and it would have shipped a keyboard
   * trap in plain sight for anyone not using a mouse. The white offset sits between
   * the control and the ring, so the indicator is perceivable against ANY background:
   * white-on-accent is 6.38:1 and white-on-danger 6.57:1, while the blue ring itself
   * is 6.23:1 against the page behind it.
   *
   * Consumers must render both. `style` below does it in one declaration.
   */
  offsetColor: palette.surface,

  /** Two-tone ring via layered box-shadows: control, white gap, blue ring. */
  style: `0 0 0 2px ${palette.surface}, 0 0 0 5px ${color.focus}`,
} as const;
